import React, { useMemo } from "react";
import { Box, Link, Paper, Stack, TableContainer, Typography, useTheme } from "@mui/material";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import TextModule from "@/components/common/text-module/text-module";
import type { OfferSavingsTable as OfferSavingsTableData } from "@/types/dto/landing-page/landing-page";

export interface OfferSavingsTableProps {
  offerSavingsTable: OfferSavingsTableData;
  moduleRankingPosition?: string;
  moduleId?: string;
}

const hasParagraphContent = (children: React.ReactNode) =>
  React.Children.toArray(children).some(child => React.isValidElement(child) || (typeof child === "string" && child.trim().length > 0));

const createTableRichTextOptions = (tableHeader: string) => ({
  renderNode: {
    [BLOCKS.TABLE]: ({ content }: any, children: React.ReactNode) => {
      const rows = React.Children.toArray(children);
      const headerRowCount = content.filter(
        (row: any) => row.content?.every((cell: any) => cell.nodeType === BLOCKS.TABLE_HEADER_CELL)
      ).length;

      return (
        <table>
          <caption
            style={{
              backgroundColor: "#1E3D4A",
              backgroundImage: "url('https://images.ctfassets.net/69jewsgul74b/2C2zTjH8GZVpuv4hc4pLWo/b4b28512dd9707c16674979bc60ee3ec/footeremailpattern.png'), url('/images/Pattern.svg')",
              color: "#fff",
              fontSize: "clamp(2rem, 3vw, 2.8rem)",
              fontWeight: 700,
              lineHeight: 1.2,
              padding: "2.8rem 2.4rem",
              textAlign: "center",
              textTransform: "uppercase"
            }}
          >
            {tableHeader}
          </caption>
          {headerRowCount > 0 && <thead>{rows.slice(0, headerRowCount)}</thead>}
          <tbody>{rows.slice(headerRowCount)}</tbody>
        </table>
      );
    },
    [BLOCKS.PARAGRAPH]: (_node: any, children: React.ReactNode) => (hasParagraphContent(children) ? <p>{children}</p> : null),
    [BLOCKS.TABLE_HEADER_CELL]: (node: any, children: React.ReactNode) => <th scope="col">{children}</th>,
    [INLINES.HYPERLINK]: ({ data: { uri } }: any, children: React.ReactNode) => (
      <Link href={uri} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    )
  }
});

const OfferSavingsTableRichText = ({ section }: { section: OfferSavingsTableData["salesTable"]["richTextTable"][number] }) => {
  const options = useMemo(() => createTableRichTextOptions(section.tableHeader), [section.tableHeader]);
  const theme = useTheme();

  return (
    <Box
      sx={{
        "& table tr, & table th, & table td": {
          textAlign: section.alignment || "center"
        },
        "& table th, & table td": {
          padding: "0.8rem 2.4rem"
        },
        ...(!section.alignment && {
          "& table tr > :first-child": {
            textAlign: "left"
          },
          "& table tr > :last-child": {
            textAlign: "right"
          },
          "& table tr > :nth-child(2):nth-last-child(2)": {
            textAlign: "center"
          }
        }),
        "& table tbody tr:nth-of-type(odd)": {
          borderBottom: "1px solid rgba(19, 47, 59, 0.12)",
          background: "#F3FAFC"
        },
        "& table tbody tr:nth-of-type(even)": {
          borderBottom: "1px solid rgba(19, 47, 59, 0.12)",
          background: theme.palette.background.cool
        },
        "& table tbody tr:last-of-type": {
          background: theme.palette.background.cool_strong,
          borderBottom: "none"
        },
        "& table tr:last-child td": {
          color: "#1E3D4A",
          fontWeight: 800,
          fontSize: "2rem",
          padding:"0 2.4rem"
        },
        "& table tr td": {
          fontWeight: 600,
          fontSize: "1.6rem"
        },
        "& table": {
          borderCollapse: "collapse",
          borderSpacing: 0,
          width: "100%"
        }
      }}
    >
      {documentToReactComponents(section.table, options)}
    </Box>
  );
};

const OfferSavingsTable = ({
  offerSavingsTable,
  moduleRankingPosition,
  moduleId
}: OfferSavingsTableProps) => (
  <Stack
    component="section"
    className="pattern-bg destination-page-container"
    data-testid="offer-savings-table"
    data-module-name="Offer Savings Table"
    data-parent-container-type="Landing Page"
    data-module-ranking-position={moduleRankingPosition}
    data-module-id={moduleId}
    sx={{ padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" } }}
  >
    
    <TextModule
      basicInfo={{ ...offerSavingsTable.textBlockComponent, ctas: [] }}
      moduleName="Offer Savings Table Text"
      moduleParentContainerType="Landing Page"
      slotProps={{
        root: { sx: { padding: 0 } },
        container: { sx: { maxWidth: "75rem", width: "100%" } },
        header: { sx: { textAlign: "center" } },
        copy: { sx: { textAlign: "center" } }
      }}
    />

    <Typography component="h3" variant="h3" align="center" sx={{ my: { xs: "2.4rem", md: "4rem" } }} color="primary.main" fontWeight={600}>
      {offerSavingsTable.salesTable.header}
    </Typography>

    <Box
      sx={{
        display: "grid",
        gap: "2.4rem",
        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
        width: "100%",
        maxWidth: "103rem",
        margin: "0 auto"
      }}
    >
      {offerSavingsTable.salesTable.richTextTable?.map(section => (
        <TableContainer
          component={Paper}
          key={section.tableHeader}
          sx={{
            borderRadius: "1.8rem",
            boxShadow: "0 3px 6px 0 rgba(0, 0, 0, 0.10)",
            overflow: "auto"
          }}
        >
          <OfferSavingsTableRichText section={section} />
        </TableContainer>
      ))}
    </Box>

    {offerSavingsTable.textBlockComponent.ctas?.length ? (
      <TextModule
        basicInfo={offerSavingsTable.textBlockComponent}
        hideText
        moduleName="Offer Savings Table CTAs"
        moduleParentContainerType="Landing Page"
        slotProps={{
          root: { sx: { padding: { xs: "4rem 0 0", md: "6rem 0 0" } } },
          container: {
            sx: {
              maxWidth: "none",
              width: "100%"
            }
          },
          ctaContainer: {
            sx: {
              display: "flex",
              flexWrap: "wrap",
              gap: "2.4rem",
              justifyContent: "center",
              alignItems: "center"
            }
          }
        }}
      />
    ) : null}
  </Stack>
);

export default OfferSavingsTable;
